
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useBuyer } from "@/contexts/BuyerContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { validateImage } from "@/utils/imageProcessing";
import { generateInitials } from "@/utils/userUtils";
import ImageCropDialog from "@/components/common/ImageCropDialog";

const BuyerProfileImageSection = () => {
  const { buyerProfile, updateBuyerProfile } = useBuyer();
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);

  const handleProfileImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !buyerProfile) return;
    
    // Reset the input value to allow re-selecting the same file
    e.target.value = '';
    
    // Validate the image file
    const errorMsg = validateImage(file, 5); // 5MB limit
    if (errorMsg) {
      toast({
        title: "Invalid image",
        description: errorMsg,
        variant: "destructive"
      });
      return;
    }
    
    // Create object URL for crop dialog
    const imageUrl = URL.createObjectURL(file);
    setSelectedImage(imageUrl);
    setShowCropDialog(true);
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    if (!buyerProfile) return;
    
    try {
      setIsUploading(true);
      setShowCropDialog(false);
      
      // Preview the cropped image
      setPreviewImage(URL.createObjectURL(croppedBlob));
      
      console.log("Starting image upload for buyer:", buyerProfile.id);
      
      // Create a unique file name with user folder structure
      const fileName = `${buyerProfile.user_id}/${Date.now()}.jpg`;
      
      console.log("Uploading to path:", fileName);
      
      // Upload the cropped image to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('profiles')
        .upload(fileName, croppedBlob, {
          contentType: 'image/jpeg',
          upsert: true
        });
        
      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error(`Upload failed: ${uploadError.message}`);
      }
      
      // Get the public URL
      const { data } = supabase.storage
        .from('profiles')
        .getPublicUrl(fileName);
        
      console.log("Got public URL:", data?.publicUrl);
        
      // Update the buyer profile with the new image URL
      if (data?.publicUrl) {
        console.log("Updating buyer profile with image URL:", data.publicUrl);
        await updateBuyerProfile({
          profile_image_url: data.publicUrl
        });
        
        toast({
          title: "Profile image updated",
          description: "Your profile image has been updated successfully."
        });
      } else {
        throw new Error('Failed to get public URL for uploaded image');
      }
    } catch (error: any) {
      console.error('Profile image upload error:', error);
      toast({
        title: "Upload failed",
        description: error.message || "An error occurred while uploading your profile image.",
        variant: "destructive"
      });
      setPreviewImage(null);
    } finally {
      setIsUploading(false);
      // Clean up object URLs
      if (selectedImage) {
        URL.revokeObjectURL(selectedImage);
        setSelectedImage(null);
      }
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    }
  };

  // Generate user initials from profile name
  const userInitials = generateInitials(buyerProfile?.full_name);

  // Determine which image to show (preview, existing, or fallback)
  const displayImage = previewImage || buyerProfile?.profile_image_url;

  console.log("Current buyer profile image URL:", buyerProfile?.profile_image_url);
  console.log("Display image:", displayImage);

  return (
    <>
      <ImageCropDialog
        open={showCropDialog}
        imageSrc={selectedImage || ''}
        onClose={() => {
          setShowCropDialog(false);
          if (selectedImage) {
            URL.revokeObjectURL(selectedImage);
            setSelectedImage(null);
          }
        }}
        onCropComplete={handleCropComplete}
      />
      
      <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
          <div className="relative">
            {/* Fixed-size avatar with aspect ratio */}
            <div className="w-24 h-24">
              <AspectRatio ratio={1} className="bg-muted rounded-full overflow-hidden">
                <Avatar className="w-full h-full">
                  <AvatarImage 
                    src={displayImage || ""} 
                    alt={buyerProfile?.full_name || "User"} 
                    className="object-cover"
                  />
                  <AvatarFallback className="bg-godyspo-blue text-white text-2xl">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
              </AspectRatio>
            </div>
            
            {/* Upload indicator */}
            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <h3 className="text-lg font-medium">{buyerProfile?.full_name || "User"}</h3>
            <p className="text-sm text-muted-foreground">Buyer Account</p>
            <div>
              <input
                id="buyerProfileImage"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleProfileImageSelect}
                disabled={isUploading}
              />
              <label
                htmlFor="buyerProfileImage"
                className={`inline-flex items-center px-4 py-2 bg-godyspo-blue text-white text-sm font-medium rounded-md cursor-pointer transition-colors ${
                  isUploading 
                    ? "opacity-50 cursor-not-allowed" 
                    : "hover:bg-godyspo-blue/90"
                }`}
              >
                {isUploading ? "Uploading..." : "Upload Photo"}
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              For best results, upload a square image (jpg, png, gif, webp). Max 5MB. Will be cropped to a circle.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
    </>
  );
};

export default BuyerProfileImageSection;
